import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { BillingInfoInput, Query } from "@onmo/graphql/graphql";
import {
  INDIAN_STATES,
  SPANISH_PROVINCES,
  SPANISH_TOWNS_URL,
  StateOption,
} from "../../../constants/state";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { meAAAGQL } from "../../../graphql";
import {
  BillingRegion,
  isValidSpanishPostalCode,
  isValidSpanishTaxId,
  normalizeSpanishTaxId,
} from "../../../models/billing";
import { Dialog } from "../../core/Dialog/Dialog";
import { ITextFieldProps, TextField } from "../../core/TextField/TextField";
import { Typography } from "../../core/Typography/Typography";
import { BillingSelect } from "./BillingSelect";
import { useStyles } from "./BillingInfoPopup.style";

type BillingInfoPopupProps = {
  open: boolean;
  region?: BillingRegion;
  onClose?: () => void;
  onSkip?: () => void;
  onContinue?: (data: BillingInfoInput) => void | Promise<void>;
};

type BillingForm = { [Field in keyof BillingInfoInput]-?: string };
type BillingField = keyof BillingForm;
type BillingSelectField = "state" | "city";

const hasOption = (options: StateOption[], value: string) =>
  options.some((option) => option.value === value);

const BillingInfoPopup: React.FC<BillingInfoPopupProps> = ({
  open,
  region = "IN",
  onClose,
  onSkip,
  onContinue,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: meData } = useQuery<{ me: Query["me"] }>(meAAAGQL);
  const billingInfo = meData?.me?.metadata?.billingInfo;
  const isSpain = region === "ES";
  const savedForm = useMemo<BillingForm>(
    () => ({
      name: billingInfo?.name ?? "",
      state: billingInfo?.state ?? "",
      taxId: billingInfo?.taxId ?? "",
      address: billingInfo?.address ?? "",
      postalCode: billingInfo?.postalCode ?? "",
      city: billingInfo?.city ?? "",
    }),
    [
      billingInfo?.name,
      billingInfo?.state,
      billingInfo?.taxId,
      billingInfo?.address,
      billingInfo?.postalCode,
      billingInfo?.city,
    ],
  );
  const [form, setForm] = useState(savedForm);
  const [touched, setTouched] = useState<Partial<Record<BillingField, true>>>(
    {},
  );
  const [openSelect, setOpenSelect] = useState<BillingSelectField | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [spanishTowns, setSpanishTowns] = useState<Record<string, string[]>>();

  useEffect(() => {
    if (!open) return;
    setForm(savedForm);
    setTouched({});
  }, [open, savedForm]);

  useEffect(() => {
    if (!open || !isSpain || spanishTowns) return;
    const controller = new AbortController();
    fetch(SPANISH_TOWNS_URL, { signal: controller.signal })
      .then((res) => res.json() as Promise<Record<string, string[]>>)
      .then(setSpanishTowns)
      .catch((err) => {
        if (err?.name !== "AbortError") {
          console.error("Failed to load Spanish towns", err);
        }
      });
    return () => controller.abort();
  }, [open, isSpain, spanishTowns]);

  const townOptions = useMemo<StateOption[]>(
    () =>
      (spanishTowns?.[form.state] ?? []).map((town) => ({
        label: town,
        value: town,
      })),
    [spanishTowns, form.state],
  );

  const isTaxIdValid = isValidSpanishTaxId(form.taxId);
  const isPostalCodeValid = isValidSpanishPostalCode(form.postalCode);

  const isValid = isSpain
    ? !!form.name.trim() &&
      isTaxIdValid &&
      !!form.address.trim() &&
      isPostalCodeValid &&
      hasOption(SPANISH_PROVINCES, form.state) &&
      hasOption(townOptions, form.city)
    : !!form.name.trim() && !!form.state;

  const toPayload = (): BillingInfoInput =>
    isSpain
      ? {
          name: form.name.trim(),
          taxId: normalizeSpanishTaxId(form.taxId),
          address: form.address.trim(),
          postalCode: form.postalCode.trim(),
          state: form.state,
          city: form.city,
        }
      : { name: form.name.trim(), state: form.state };

  const handleContinue = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onContinue?.(toPayload());
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(savedForm);
    setTouched({});
    setOpenSelect(null);
  };

  const onCloseInternal = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSkip = () => {
    if (submitting) return;
    reset();
    onSkip?.();
  };

  const setField = (field: BillingField) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const selectProvince = (state: string) =>
    setForm((prev) => ({
      ...prev,
      state,
      city: prev.state === state ? prev.city : "",
    }));

  const toggleSelect = (field: BillingSelectField) => (isOpen: boolean) =>
    setOpenSelect(isOpen ? field : null);

  const errorFor = (
    field: BillingField,
    isFieldValid: boolean,
    message: string,
  ) => (touched[field] && form[field] && !isFieldValid ? message : undefined);

  const renderTextField = (
    field: BillingField,
    label: string,
    placeholder: string,
    props: Pick<ITextFieldProps, "maxLength"> & {
      error?: string;
      inputMode?: "numeric";
    } = {},
  ) => (
    <div className={classes.field}>
      <Typography
        variant={TypographyVariant.BodyB5Regular}
        color={TypographyColor.Silver}
        className={classes.label}
      >
        {label}
      </Typography>
      <TextField
        fullWidth
        inputProps={{
          placeholder,
          "aria-label": label,
          value: form[field],
          inputMode: props.inputMode,
          dataGamepad: openSelect ? undefined : `billing-${field}`,
        }}
        maxLength={props.maxLength}
        isError={!!props.error}
        descriptionMessage={props.error}
        onChangeValue={setField(field)}
        onBlur={() => setTouched((prev) => ({ ...prev, [field]: true }))}
        className={classes.input}
      />
    </div>
  );

  const title = isSpain
    ? t("Payment_Invoice Details")
    : t("Payment_Enter Your Details");

  return (
    <Dialog
      open={open}
      onClose={onCloseInternal}
      title={title}
      className={classes.dialog}
      mainAction={{
        text: t("Payment_Continue"),
        onClick: handleContinue,
        disabled: !isValid || submitting,
        loading: submitting,
        disableGamepadAction: !!openSelect,
      }}
      secondAction={
        isSpain
          ? {
              text: t("Payment_Skip"),
              onClick: handleSkip,
              disabled: submitting,
              disableGamepadAction: !!openSelect,
            }
          : undefined
      }
    >
      <div className={classes.header}>
        <Typography
          variant={TypographyVariant.HeadingH3Bold}
          color={TypographyColor.Default}
        >
          {title}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB3Medium}
          color={TypographyColor.Default}
        >
          {isSpain
            ? t(
                "Payment_Add your details for a full invoice, or skip to receive a simplified invoice.",
              )
            : t(
                "Payment_Before payment, please enter your name and state for your payment receipt.",
              )}
        </Typography>
      </div>

      {isSpain ? (
        <div className={classes.form}>
          {renderTextField(
            "name",
            t("Payment_Full Name"),
            t("Payment_Enter Your Name"),
          )}
          {renderTextField(
            "taxId",
            t("Payment_NIF/NIE"),
            t("Payment_Enter NIF/NIE"),
            {
              maxLength: 9,
              error: errorFor(
                "taxId",
                isTaxIdValid,
                t("Payment_Enter a valid NIF/NIE"),
              ),
            },
          )}
          {renderTextField(
            "address",
            t("Payment_Address"),
            t("Payment_Enter Your Address"),
          )}
          {renderTextField(
            "postalCode",
            t("Payment_Post Code"),
            t("Payment_Enter Your Post Code"),
            {
              maxLength: 5,
              inputMode: "numeric",
              error: errorFor(
                "postalCode",
                isPostalCodeValid,
                t("Payment_Enter a valid 5-digit post code"),
              ),
            },
          )}
          <BillingSelect
            id="billing-province-listbox"
            label={t("Payment_Province")}
            placeholder={t("Payment_Choose Your Province")}
            options={SPANISH_PROVINCES}
            value={form.state}
            onChange={selectProvince}
            isOpen={openSelect === "state"}
            onOpenChange={toggleSelect("state")}
            gamepadId={openSelect ? undefined : "province-select"}
          />
          <BillingSelect
            id="billing-town-listbox"
            label={t("Payment_Town")}
            placeholder={t("Payment_Choose Your Town")}
            options={townOptions}
            value={form.city}
            onChange={setField("city")}
            isOpen={openSelect === "city"}
            onOpenChange={toggleSelect("city")}
            gamepadId={openSelect ? undefined : "town-select"}
            disabled={!townOptions.length}
          />
        </div>
      ) : (
        <div className={classes.form}>
          {renderTextField(
            "name",
            t("Payment_Full Name"),
            t("Payment_Enter Your Name"),
          )}
          <BillingSelect
            id="billing-state-listbox"
            label={t("Payment_State")}
            placeholder={t("Payment_Choose Your State")}
            options={INDIAN_STATES}
            value={form.state}
            onChange={setField("state")}
            isOpen={openSelect === "state"}
            onOpenChange={toggleSelect("state")}
            gamepadId={openSelect ? undefined : "state-select"}
          />
        </div>
      )}
    </Dialog>
  );
};

export default BillingInfoPopup;
